import React, { ReactNode } from "react"
import {
  CheckCircle,
  mergeOverrides,
  NumberedStep as BaseNumberedStep,
  NumberedStepOverrides,
  ParagraphSmall,
  ProgressSteps as BaseProgressSteps,
  Spinner,
  StyledNumberIcon,
  useStyletron,
} from "@mezo-org/mezo-clay"

function StepIcon(props: {
  $isActive: boolean
  $isCompleted: boolean
  isPending: boolean
  $isCurrent: boolean
  children: ReactNode
}) {
  const {
    $isActive,
    $isCompleted,
    isPending,
    $isCurrent,
    children,
    ...restProps
  } = props

  const [_, theme] = useStyletron()

  const renderContent = () => {
    if ($isCompleted) {
      return <CheckCircle size={16} checkMarkColor={theme.colors.black} />
    }

    if ($isActive && isPending) {
      return <Spinner $size={22} {...restProps} />
    }

    return children
  }

  return (
    <StyledNumberIcon
      {...restProps}
      $isActive={$isActive}
      $isCompleted={$isCompleted}
      $isCurrent={$isCurrent}
      style={{ height: "36px", width: "36px" }}
    >
      {renderContent()}
    </StyledNumberIcon>
  )
}

export function NumberedStep({
  title,
  isPending = false,
  pendingText = "Waiting for transaction...",
  ...restProps
}: {
  title: string
  isPending?: boolean
  pendingText?: ReactNode
  overrides?: NumberedStepOverrides
}) {
  const mergedOverrides = mergeOverrides(
    {
      Icon: {
        component: StepIcon,
        props: {
          isPending,
        },
      },
    },
    restProps.overrides,
  )

  return (
    <BaseNumberedStep {...restProps} overrides={mergedOverrides} title={title}>
      {isPending &&
        (typeof pendingText === "string" ? (
          <ParagraphSmall>{pendingText}</ParagraphSmall>
        ) : (
          pendingText
        ))}
    </BaseNumberedStep>
  )
}

export default function ProgressSteps({
  currentStep,
  children,
}: {
  children: ReactNode
  currentStep: number
}) {
  return (
    <BaseProgressSteps
      current={currentStep}
      overrides={{
        Root: { style: { marginTop: "auto" } },
        Title: {
          style: ({ $theme }) => ({
            paddingTop: $theme.sizing.scale650,
            paddingBottom: $theme.sizing.scale100,
          }),
        },
        Tail: {
          style: ({ $theme }) => {
            const size = $theme.sizing.scale950
            const titleFont = $theme.typography.LabelMedium
            const titlePad = $theme.sizing.scale650

            return {
              left: "25px",
              top: 0,
              // TODO: how to calculate the height correctly?
              height: "100%",
              marginTop: `calc(${titlePad} + ${size} + (${titleFont.lineHeight} - ${size}) / 2)`,
            }
          },
        },
        Icon: {
          component: StepIcon,
          style: ({ $theme }) => ({
            width: $theme.sizing.scale950,
            height: $theme.sizing.scale950,
            border: "none",
            ":before": { border: "none" },
          }),
        },
      }}
    >
      {children}
    </BaseProgressSteps>
  )
}
